import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

export interface Attribution {
  // Internal tracking link params
  channel: string;
  campaign: string | null;
  variant: string | null;
  referrer: string | null;

  // Ad platform click IDs
  fbclid: string | null;        // Meta Ads
  gclid: string | null;         // Google Ads
  ttclid: string | null;        // TikTok Ads
  line_click_id: string | null; // LINE Ads

  // UTM (utm_source/medium/campaign overlap with channel/campaign — we keep extra fields)
  utm_content: string | null;
  utm_term: string | null;
}

const STORAGE_KEY = 'meso_attribution_v2';

const EMPTY: Attribution = {
  channel: 'direct', campaign: null, variant: null, referrer: null,
  fbclid: null, gclid: null, ttclid: null, line_click_id: null,
  utm_content: null, utm_term: null,
};

/**
 * อ่าน URL params: internal (?ch=&cmp=&v=&ref=) + UTM (utm_*) + click IDs (fbclid/gclid/ttclid)
 * Persist ใน sessionStorage เพื่อกันหายตอน navigate ใน LIFF
 */
export function useAttribution(): Attribution {
  const [searchParams] = useSearchParams();

  return useMemo(() => {
    const get = (k: string) => searchParams.get(k);

    // Prefer internal tracking link params; fall back to utm_*
    const channel  = get('ch')         ?? get('utm_source')   ?? '';
    const campaign = get('cmp')        ?? get('utm_campaign') ?? null;
    const variant  = get('v')          ?? get('utm_medium')   ?? null;
    const referrer = get('ref')                                ?? null;

    const fromUrl: Attribution = {
      channel, campaign, variant, referrer,
      fbclid:        get('fbclid'),
      gclid:         get('gclid'),
      ttclid:        get('ttclid'),
      line_click_id: get('line_click_id') ?? get('lineclid'),
      utm_content:   get('utm_content'),
      utm_term:      get('utm_term'),
    };

    // Has any signal? persist
    const hasSignal = channel || fromUrl.fbclid || fromUrl.gclid || fromUrl.ttclid ||
                      fromUrl.line_click_id || campaign || referrer;

    if (hasSignal) {
      try { sessionStorage.setItem(STORAGE_KEY, JSON.stringify(fromUrl)); } catch {/* ignore */}
      // default channel if only ad ID present
      return { ...fromUrl, channel: channel || 'direct' };
    }

    try {
      const cached = sessionStorage.getItem(STORAGE_KEY);
      if (cached) return JSON.parse(cached) as Attribution;
    } catch {/* ignore */}

    return EMPTY;
  }, [searchParams]);
}
